<template>
  <v-menu
    offset-y
    :close-on-content-click="false"
    v-model="menuOpen"
    @update:model-value="onMenuToggle"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" style="cursor: pointer">
        <slot></slot>
      </div>
    </template>

    <v-card width="340">
      <v-list class="pa-0">
        <v-list-item class="px-7 pt-3 pb-0">
          <v-row>
            <v-col cols="12">
              <span class="text-subtitle-1 font-weight-bold"
                >My Searches({{ totalSearchesCount }})</span
              >
            </v-col>
          </v-row>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <!-- Loading state -->
        <template v-if="isLoading">
          <v-list-item
            class="d-flex flex-column align-center justify-center py-6"
          >
            <v-progress-circular
              indeterminate
              size="40"
              class="mb-3"
            ></v-progress-circular>
            <div class="text-subtitle-2">Loading searches...</div>
          </v-list-item>
        </template>

        <!-- Error state -->
        <template v-else-if="hasError">
          <v-list-item
            class="d-flex flex-column align-center justify-center py-6"
          >
            <v-icon color="error" size="40" class="mb-3"
              >mdi-alert-circle</v-icon
            >
            <div class="text-subtitle-2 text-error">{{ errorMessage }}</div>
          </v-list-item>
        </template>

        <!-- Searches list (showing first 5) -->
        <template v-else-if="searchItems?.length">
          <template v-for="(group, index) in searchItems" :key="group.id">
            <v-list-item class="px-4" @click="goTo(group)">
              <v-row>
                <v-col cols="3">
                  <v-list-item-avatar size="60">
                    <v-img
                      style="border-radius: 5px !important"
                      width="100"
                      height="100"
                      :src="getSearchImage(group.preview) || defaultIcon"
                      :alt="group.name"
                    />
                  </v-list-item-avatar>
                </v-col>

                <v-col cols="9" style="margin-top: 20px">
                  <v-list-item-content style="cursor: pointer">
                    <v-list-item-title class="text-body-2 font-weight-medium">
                      {{ group.name }}
                    </v-list-item-title>

                    <v-list-item-title class="text-body-2 font-weight-medium">
                      {{ group.preview?.city_name || "" }}
                      <span v-if="group.preview?.keyword">
                        | keyword: {{ group.preview.keyword }}</span
                      >
                      <!-- <span v-if="group.count"> ({{ group.count }})</span> -->
                    </v-list-item-title>

                    <v-list-item-subtitle>
                      Saved on:
                      <small class="text-black">{{
                        formatDate(group.latestSavedAt)
                      }}</small>
                      <br />
                      <small class="text-black">
                        {{ group.preview?.title }}
                      </small>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-col>
              </v-row>
            </v-list-item>
          </template>
        </template>

        <!-- Empty state -->
        <template v-else>
          <v-list-item
            class="d-flex flex-column align-center justify-center py-6"
          >
            <div
              style="display: flex; justify-content: center"
              class="text-center"
            >
              <v-img
                src="@/assets/empty-state-search.svg"
                width="70"
                class="mb-3"
              />
            </div>

            <div class="text-subtitle-2 font-weight-bold mb-1 flex text-center">
              You have no saved searches yet
            </div>
            <div class="text-caption text-grey text-center">
              {{
                isUserAuthenticated
                  ? "Start searching to save your preferences"
                  : "Sign in to save your searches"
              }}
            </div>
          </v-list-item>
        </template>
      </v-list>

      <v-divider></v-divider>

      <v-list-item
        @click="goToAll"
        class="text-center  font-weight-bold"
        style="color: darkgoldenrod !important"
      >
        View All Saved Searches
      </v-list-item>
    </v-card>
  </v-menu>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { API_URL } from "@/helpers/api";

const props = defineProps({
  listings: { type: Array, default: () => [] },
});


console.log(
  "🔍 SearchesDropdown component initialized with listings:",
  props.listings
);

const searchQuery = ref("");

const defaultIcon = ref("/default-search-icon.png");
const menuOpen = ref(false);

const store = useStore();
const router = useRouter();
const route = useRoute();  

// const isLoading = computed(() => store.getters["savedSearches/isLoading"]);
const isLoading = ref(false);
const hasError = computed(() => store.getters["savedSearches/hasError"]);
const errorMessage = computed(
  () => store.getters["savedSearches/errorMessage"]
);
const isUserAuthenticated = computed(
  () => store.getters["savedSearches/isUserAuthenticated"]
);

// const searchItems = computed(() => {
//   return (props.listings || [])
//     .filter((item) => item.savedSearchId && item.fromSavedSearch)
//     .slice(0, 3);
// });

// const totalSearchesCount = computed(() => {
//   return (props.listings || []).length;
// });

const generateSlug = (title) => {
  if (!title) return "listing";
  return title
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
    .replace(/^-|-$/g, "");
};

const groupedBySearch = computed(() => {
  const map = new Map();
  for (const it of props.listings || []) {
    if (!it?.savedSearchId) continue; // ignore non-saved-search items (e.g., “similar”)
    const cur = map.get(it.savedSearchId);
    if (!cur) {
      map.set(it.savedSearchId, {
        id: it.savedSearchId,
        name: it.fromSavedSearch,
        latestSavedAt: it.savedAt,
        count: 1,
        preview: it, // use the first as preview for image/route
      });
    } else {
      cur.count += 1;
      // keep the latest-saved item as preview
      if (
        !cur.latestSavedAt ||
        new Date(it.savedAt) > new Date(cur.latestSavedAt)
      ) {
        cur.latestSavedAt = it.savedAt;
        cur.preview = it;
      }
    }
  }
  // newest first
  return Array.from(map.values()).sort(
    (a, b) => new Date(b.latestSavedAt) - new Date(a.latestSavedAt)
  );
});

const searchItems = computed(() => groupedBySearch.value.slice(0, 3)); // top 3 searches
const totalSearchesCount = computed(() => groupedBySearch.value.length);

// const goTo = async (item) => {
//   console.log("Item in dropdown", item);
//   const savedTitle = item?.title || "";

//   searchQuery.value = savedTitle;
//   const categorySlug = generateSlug(item.category?.name);

//   // Dispatch search object
//   await store.dispatch("FilterStore/setSelectedListingItem", item);

//   if (item.category?.id === 3 || item.category?.id === 4) {
//     return router.push({
//       name: "PropertySearch",
//       params: { categorySlug },
//       query: {
//         categoryId: item.category?.id,
//         subcategoryId: item.subcategory?.id,
//         source: "my-searches-dropdown",
//       },
//     });
//   }

//   // Default route for other categories
//   return router.push({
//     name: "searchResults",
//     query: {
//       q: searchQuery.value,
//       categoryId: item.category?.id,
//       subcategoryId: item.subcategory?.id,
//       source: "my-searches-dropdown",
//     },
//   });

//   menuOpen.value = false;
// };

// const goTo = async (group) => {
//   console.log("Group item", group);
//   // const item = group?.preview;
//   // if (!item) return;

//   // await store.dispatch("FilterStore/setSelectedListingItem", item);

//   // const categorySlug = generateSlug(item.category?.name);
//   // if (item.category?.id === 3 || item.category?.id === 4) {
//   //   return router.push({
//   //     name: "PropertySearch",
//   //     params: { categorySlug },
//   //     query: {
//   //       categoryId: item.category?.id,
//   //       subcategoryId: item.subcategory?.id,
//   //       source: "my-searches-dropdown",
//   //     },
//   //   });
//   // }

//   // return router.push({
//   //   name: "searchResults",
//   //   query: {
//   //     q: item.title || "",
//   //     categoryId: item.category?.id,
//   //     subcategoryId: item.subcategory?.id,
//   //     source: "my-searches-dropdown",
//   //   },
//   // });
// };


async function goTo(item) {

  console.log("Item is dropdown", item);
  //  menuOpen.value = false;
  // const catId = item.preview?.category?.id;
  // const subId =
  //   item.preview?.subcategory?.id;

  // const keyword = (item.preview?.keyword || "").trim(); // use saved search keyword (broad), not a listing title

  // const isProperty = catId === 3 || catId === 4;
  // const categorySlug = generateSlug(
  //   item.preview?.category?.name || "category"
  // );

  // // store the whole search object if you need it later
  // await store.dispatch("FilterStore/setSelectedListingItem", null);

  // const query = {
  //   ...(keyword ? { q: keyword } : {}), // omit q if empty
  //   categoryId: catId,
  //   subcategoryId: subId,
  //   source: "saved-search",
  // };

  // if (isProperty) {
  //   return router.push({
  //     name: "PropertySearch",
  //     params: { categorySlug },
  //     query,
  //   });
  // }
  // return router.push({ name: "searchResults", query });

menuOpen.value = false;

  const preview = item.preview || {};
  const cat      = preview.category || {};
  const sub      = preview.subcategory || {};
  const group    = preview.group || preview.propertyTypeGroup || preview.propertyType || {};

  const catId    = cat.id ?? null;
  const subId    = sub.id ?? null;
  const groupId  = group.id ?? null;

  const keyword  = (preview.keyword || "").trim();
  const catSlug  = cat.slug || generateSlug(cat.name || "category");


  const isProperty = catId === 3 || catId === 4;

  // Canonical query that both “General” and “Property” pages understand
  const query = {
    ...(keyword ? { q: keyword } : {}),
    ...(catId ? { categoryId: catId } : {}),
    ...(subId ? { subcategoryId: subId } : {}),
    ...(groupId ? { groupId } : {}),
    source: "saved-search",
    // let the target page hydrate exactly from the saved-search if it wants to
    savedSearchId: item.id, // safe even if target ignores it
  };

  await store.dispatch("FilterStore/setSelectedListingItem", null);

  if (isProperty) {
    return router.push({
      name: "PropertySearch",
      params: { categorySlug: catSlug },
      query,
    });
  }

  return router.push({
    name: "searchResults",
    query,
  });
  
}




const goToAll = () => {
  router.push("/my-searches");
  menuOpen.value = false;
};

const deleteSearch = async (searchId) => {
  if (!confirm("Are you sure you want to delete this search?")) return;
  try {
    await store.dispatch("savedSearches/deleteSearch", searchId);
  } catch (error) {
    console.error("Failed to delete search:", error);
  }
};

const formatDate = (date) => {
  if (!date) return "";
  try {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

// const getSearchImage = (item) => {
//   return item.images?.[0] || defaultIcon.value;
// };

const getSearchImage = (item) => {
  const img = item?.images?.[0] || item?.cover_image || item?.thumbnail;
  if (!img) return defaultIcon.value;
  // If your API sometimes returns relative paths, normalize here:
  if (typeof img === "string" && !/^https?:\/\//i.test(img)) {
    return `${API_URL.replace(/\/+$/, "")}/${img.replace(/^\/+/, "")}`;
  }
  return img;
};

const onMenuToggle = (isOpen) => {
  if (isOpen) {
    console.log("Dropdown opened with listings:", props.listings);
  }
};

// onMounted(async () => {
//   //Fetch saved searches
//   try {
//     console.log("🔍 Initializing saved searches for all users aaaa...");
//     await store.dispatch("savedSearches/fetchSavedSearches");
//     console.log("✅ Saved searches fetched successfully");
//   } catch (err) {
//     console.error("❌ Failed to fetch saved searches:", err);
//   }
// });

onMounted(() => {
  console.log("Search Items", searchItems.value);
  console.log("Preview Search",groupedBySearch.value);
});

// ✅ ensures the menu never stays open after route navigation
watch(() => route.fullPath, () => (menuOpen.value = false));

</script>

<style scoped>
.v-list-item {
  min-height: 60px;
}

.v-list-item-avatar {
  margin-right: 12px;
}

.v-list-item-content {
  flex: 1;
}

.v-list-item-action {
  margin-left: 8px;
}
</style>
